/**
 * MessageBlockRenderer - Renders parsed SSE events as flat timeline
 *
 * Supports content block types:
 * - tool_use: Tool calls with request/response (expandable)
 * - tool_result: Tool results
 * - thinking: AI thinking process (shown directly)
 * - text: Text response
 * - approval_request: HITL approval UI
 */

import { useState, useMemo, useEffect, useRef, useCallback, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { Copy, Check, AlertTriangle, RefreshCw, FileText } from 'lucide-react';
import ChartBlock from './ChartBlock';
import FilesBlock from './FilesBlock';
import { WidgetRenderer, WidgetSkeleton } from './widgets';
import VisualizeWidget from './widgets/VisualizeWidget';
import { parseContentWithWidgets } from '../utils/widgetParser';
import { QuestionUI } from './QuestionUI';
import { useMarkdownPreset } from './markdown/MarkdownSizeContext';

const buildMarkdownCodeComponent = () => ({ className, children }) => {
    const codeString = String(children).replace(/\n$/, '');
    const hasLanguage = /language-(\w+)/.test(className || '');
    const isMultiLine = codeString.includes('\n');
    if (hasLanguage) return <CodeBlock className={className}>{children}</CodeBlock>;
    if (isMultiLine) return <PlainCodeBlock>{children}</PlainCodeBlock>;
    return <InlineCode>{children}</InlineCode>;
};

const buildTextMarkdownComponents = (preset) => ({
    h1: ({ children }) => <h1 className={preset.h1}>{children}</h1>,
    h2: ({ children }) => <h2 className={preset.h2}>{children}</h2>,
    h3: ({ children }) => <h3 className={preset.h3}>{children}</h3>,
    h4: ({ children }) => <h4 className={preset.h4}>{children}</h4>,
    h5: ({ children }) => <h5 className={preset.h5}>{children}</h5>,
    h6: ({ children }) => <h6 className={preset.h6}>{children}</h6>,
    p: ({ children }) => <p className={preset.p}>{children}</p>,
    ul: ({ children }) => <ul className={preset.ul}>{children}</ul>,
    ol: ({ children }) => <ol className={preset.ol}>{children}</ol>,
    li: ({ children }) => <li className={preset.li}>{children}</li>,
    strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    code: buildMarkdownCodeComponent(),
    pre: ({ children }) => <>{children}</>,
    blockquote: ({ children }) => <blockquote className={preset.blockquote}>{children}</blockquote>,
    table: ({ children }) => (
        <div className={preset.tableWrapper}>
            <table className={preset.table}>{children}</table>
        </div>
    ),
    thead: ({ children }) => <thead className="bg-gray-100/80">{children}</thead>,
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => <tr className="hover:bg-gray-50/50 transition-colors">{children}</tr>,
    th: ({ children, node }) => {
        const siblings = node?.parentElement?.children || [];
        const index = Array.from(siblings).indexOf(node);
        const isFirst = index === 0;
        const isLast = index === siblings.length - 1;
        const corners = `${isFirst ? 'rounded-tl-xl' : ''} ${isLast ? 'rounded-tr-xl' : ''}`;
        return <th className={`${preset.thBase} ${corners}`}>{children}</th>;
    },
    td: ({ children, node }) => {
        const row = node?.parentElement;
        const tbody = row?.parentElement;
        const rows = tbody?.children || [];
        const rowIndex = Array.from(rows).indexOf(row);
        const isLastRow = rowIndex === rows.length - 1;
        const siblings = row?.children || [];
        const cellIndex = Array.from(siblings).indexOf(node);
        const isFirstCell = cellIndex === 0;
        const isLastCell = cellIndex === siblings.length - 1;
        let corners = '';
        if (isLastRow && isFirstCell) corners = 'rounded-bl-xl';
        if (isLastRow && isLastCell) corners = 'rounded-br-xl';
        return <td className={`${preset.tdBase} ${corners}`}>{children}</td>;
    },
    hr: () => <hr className={preset.hr} />,
    a: ({ href, children }) => (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            {children}
        </a>
    ),
});

const buildThinkingMarkdownComponents = (preset) => ({
    h1: ({ children }) => <h1 className={preset.thinking.h1}>{children}</h1>,
    h2: ({ children }) => <h2 className={preset.thinking.h2}>{children}</h2>,
    h3: ({ children }) => <h3 className={preset.thinking.h3}>{children}</h3>,
    p: ({ children }) => <p className={preset.thinking.p}>{children}</p>,
    ul: ({ children }) => <ul className={preset.thinking.ul}>{children}</ul>,
    ol: ({ children }) => <ol className={preset.thinking.ol}>{children}</ol>,
    li: ({ children }) => <li className={preset.thinking.li}>{children}</li>,
    strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
    code: buildMarkdownCodeComponent(),
    pre: ({ children }) => <>{children}</>,
    blockquote: ({ children }) => <blockquote className={preset.thinking.blockquote}>{children}</blockquote>,
});

// Enhanced code style - vibrant colors for better readability (GitHub/VS Code inspired)
const customCodeStyle = {
    'code[class*="language-"]': {
        color: '#24292f',
        background: 'none',
        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", "Courier New", monospace',
        fontSize: '0.875rem',
        textAlign: 'left',
        whiteSpace: 'pre',
        wordSpacing: 'normal',
        wordBreak: 'normal',
        wordWrap: 'normal',
        lineHeight: '1.6',
        tabSize: 4,
        hyphens: 'none',
    },
    'pre[class*="language-"]': {
        color: '#24292f',
        background: 'none',
        overflow: 'auto',
    },
    // Comments - subtle gray
    comment: { color: '#6e7781', fontStyle: 'italic' },
    prolog: { color: '#6e7781' },
    doctype: { color: '#6e7781' },
    cdata: { color: '#6e7781' },

    // Punctuation - medium gray
    punctuation: { color: '#57606a' },
    namespace: { opacity: 0.7 },

    // Properties & Tags - green
    property: { color: '#0550ae' },
    tag: { color: '#116329' },
    'attr-name': { color: '#0550ae' },

    // Booleans & Numbers - blue
    boolean: { color: '#0a3069' },
    number: { color: '#0550ae' },
    constant: { color: '#0a3069' },
    symbol: { color: '#0550ae' },

    // Deleted/Inserted
    deleted: { color: '#cf222e', background: '#ffebe9' },
    inserted: { color: '#116329', background: '#dafbe1' },

    // Selectors - purple
    selector: { color: '#8250df' },

    // Strings - teal/blue
    string: { color: '#0a3069' },
    char: { color: '#0a3069' },
    'attr-value': { color: '#0a3069' },

    // Built-ins & Functions - purple
    builtin: { color: '#8250df' },
    function: { color: '#8250df', fontWeight: '500' },
    'class-name': { color: '#953800', fontWeight: '500' },

    // Operators & Keywords - red/pink
    operator: { color: '#cf222e' },
    keyword: { color: '#cf222e', fontWeight: '500' },
    atrule: { color: '#cf222e' },

    // Variables - orange
    variable: { color: '#953800' },

    // URLs & Entities
    entity: { color: '#0550ae', cursor: 'help' },
    url: { color: '#0969da', textDecoration: 'underline' },

    // Regex
    regex: { color: '#116329' },

    // Special
    important: { color: '#cf222e', fontWeight: 'bold' },
    bold: { fontWeight: 'bold' },
    italic: { fontStyle: 'italic' },
};
import 'katex/dist/katex.min.css';
import { formatToolName } from '../utils/helpers';

const DEBUG_ENABLED = import.meta.env.VITE_DEBUG_MODE === 'true';

const EXPANDABLE_TOOLS = new Set(['task', 'Task']);

const INLINE_TOOL_DISPLAY = {
    // web_search: { label: 'Web search', param: 'query' },
    // web_fetch: { label: 'Web fetch', param: 'url' },
};


// Clean code string - aggressively remove ALL backticks, code fences, and extra whitespace
function cleanCodeString(raw) {
    // Handle array of children (react-markdown can pass arrays)
    let result = Array.isArray(raw) ? raw.join('') : String(raw);

    // Remove code fence blocks (```language...```) if any remain
    result = result.replace(/^```[\w-]*\n?/gm, '');
    result = result.replace(/\n?```$/gm, '');

    // Iteratively peel off surrounding whitespace and backticks
    // This handles cases like: `  `code`  ` or complex nesting
    let len;
    do {
        len = result.length;
        result = result.trim();
        // Remove one layer of leading backticks
        result = result.replace(/^`+/, '');
        // Remove one layer of trailing backticks 
        result = result.replace(/`+$/, '');
    } while (result.length < len && result.length > 0);

    return result;
}

// Code Block with syntax highlighting and copy button (ChatGPT style)
function CodeBlock({ children, className }) {
    const [copied, setCopied] = useState(false);

    // Extract language from className (e.g., "language-javascript" -> "javascript")
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';
    const codeString = cleanCodeString(children);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(codeString);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    }, [codeString]);

    // Display language name (lowercase like ChatGPT)
    const displayLanguage = language?.toLowerCase() || '';

    return (
        <div className="relative my-2 rounded-xl overflow-hidden bg-[#f6f8fa]">
            {/* Header with language and copy button - ChatGPT style */}
            <div className="flex items-center justify-between px-4 py-2 text-xs text-gray-600 bg-[#f6f8fa] border-b border-gray-200/50">
                <span className="font-medium">{displayLanguage}</span>
                <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 hover:text-gray-900 transition-colors"
                >
                    {copied ? (
                        <>
                            <Check className="w-4 h-4" />
                            <span>Copied!</span>
                        </>
                    ) : (
                        <>
                            <Copy className="w-4 h-4" />
                            <span>Copy code</span>
                        </>
                    )}
                </button>
            </div>
            {/* Code content with syntax highlighting - no line backgrounds */}
            <SyntaxHighlighter
                style={customCodeStyle}
                language={language || 'text'}
                PreTag="div"
                wrapLines={false}
                wrapLongLines={false}
                customStyle={{
                    margin: 0,
                    padding: '1rem',
                    background: 'transparent',
                    fontSize: '0.875rem',
                    lineHeight: '1.6',
                }}
                codeTagProps={{
                    style: {
                        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                        background: 'none',
                    }
                }}
            >
                {codeString}
            </SyntaxHighlighter>
        </div>
    );
}

// Inline code component - simple styling without heavy background
function InlineCode({ children }) {
    const cleanContent = cleanCodeString(children);
    return (
        <span className="text-gray-800 font-mono text-[0.9em]">
            {cleanContent}
        </span>
    );
}

// Plain code block - for preformatted text without language (no header, no copy button)
function PlainCodeBlock({ children }) {
    const codeString = cleanCodeString(children);
    return (
        <div className="my-2 p-3 rounded-lg bg-[#f6f8fa] overflow-x-auto">
            <pre className="text-sm font-mono text-gray-800 leading-relaxed whitespace-pre-wrap">
                {codeString}
            </pre>
        </div>
    );
}

// Merge tool_use and tool_result blocks by tool_use_id
// Also merge child blocks (with parent_tool_use_id) into parent Task blocks
// Used for both SSE parsing and F5 history recovery
export function mergeToolBlocks(blocks) {
    const result = [];
    const toolUseMap = new Map(); // tool_use_id -> index in result array

    // First pass: merge tool_use with tool_result, collect Task blocks
    for (const block of blocks) {
        if (block.type === 'tool_use') {
            // Track this tool_use block for later merging
            if (block.id) {
                toolUseMap.set(block.id, result.length);
            }
            // Preserve existing toolResult if already merged by parseSSEEvents
            // Initialize childBlocks array for Task tool (will collect sub-agent blocks)
            const isTask = block.name === 'task' || block.name === 'Task';
            result.push({
                ...block,
                toolResult: block.toolResult !== undefined ? block.toolResult : null,
                childBlocks: isTask ? [] : undefined,
            });
        } else if (block.type === 'tool_result' && block.tool_use_id) {
            // Try to merge into existing tool_use block
            const toolUseIndex = toolUseMap.get(block.tool_use_id);
            if (toolUseIndex !== undefined && result[toolUseIndex]) {
                result[toolUseIndex].toolResult = block.content;
                result[toolUseIndex].toolResultError = block.status === 'error' || block.status === 'cancelled';
                if (block.artifact) {
                    result[toolUseIndex].artifact = block.artifact;
                }
                // Mark as merged so we can skip it in renderBlock
                block._merged = true;
                result.push(block); // Keep it but mark as merged
            } else {
                // No matching tool_use found, render as standalone
                result.push(block);
            }
        } else {
            result.push(block);
        }
    }

    // Second pass: merge child blocks (with parentToolUseId) into parent Task blocks
    const finalResult = [];
    const childBlocksToSkip = new Set();

    for (let i = 0; i < result.length; i++) {
        const block = result[i];

        // If block has parentToolUseId, try to add it to parent's childBlocks
        if (block.parentToolUseId) {
            const parentIndex = toolUseMap.get(block.parentToolUseId);
            if (parentIndex !== undefined && result[parentIndex]?.childBlocks) {
                result[parentIndex].childBlocks.push(block);
                childBlocksToSkip.add(i);
                continue;
            }
        }
    }

    // Build final result without child blocks (they're nested in parent)
    for (let i = 0; i < result.length; i++) {
        if (!childBlocksToSkip.has(i)) {
            finalResult.push(result[i]);
        }
    }

    return finalResult;
}

// Parse raw SSE events into structured blocks (preserving order)
// Merges tool_use and tool_result blocks by tool_use_id into a single block
export function parseSSEEvents(rawEvents) {
    const blocks = [];
    // Track multiple in-progress blocks by index (handles overlapping blocks)
    const inProgressBlocks = new Map();
    // Map tool_use_id to block index for merging tool_result
    const toolUseBlockMap = new Map();
    let sessionId = null;
    let durationMs = null;
    const approvalBlockMap = new Map();

    for (const rawEvent of rawEvents) {
        const eventMatch = rawEvent.match(/^event: (.+)$/m);
        const dataMatch = rawEvent.match(/^data: (.+)$/m);

        if (!dataMatch) continue;

        try {
            const data = JSON.parse(dataMatch[1]);

            switch (data.type) {
                case 'message_start':
                    sessionId = data.session_id;
                    break;

                case 'content_block_start': {
                    const newBlock = {
                        index: data.index,
                        type: data.content_block?.type,
                        ...data.content_block,
                        // Preserve original content if exists, otherwise empty string for delta accumulation
                        content: data.content_block?.content || '',
                        thinking: data.content_block?.thinking || '',
                        // For tool_use
                        input: data.content_block?.input || {},
                        // For merged tool_result
                        toolResult: null,
                        // For sub-agent blocks (from parallel tasks)
                        subagent: data.content_block?.subagent || null,
                        parentToolUseId: data.content_block?.parent_tool_use_id || null,
                    };
                    inProgressBlocks.set(data.index, newBlock);
                    break;
                }

                case 'content_block_delta': {
                    const block = inProgressBlocks.get(data.index);
                    if (block) {
                        if (data.delta?.text_delta || data.delta?.text) {
                            block.content += data.delta.text_delta || data.delta.text || '';
                        }
                        if (data.delta?.thinking_delta || data.delta?.thinking) {
                            block.thinking += data.delta.thinking_delta || data.delta.thinking || '';
                        }
                        // For tool_use input delta
                        if (data.delta?.partial_json) {
                            block.partialJson = (block.partialJson || '') + data.delta.partial_json;
                        }
                        // For approval_request (supports both intent-based and action_requests format)
                        if (data.delta?.intent) {
                            block.intent = data.delta.intent;
                            block.urgency = data.delta.urgency;
                            block.draftResponse = data.delta.draft_response;
                        }
                        if (data.delta?.action_requests) {
                            block.actionRequests = data.delta.action_requests;
                            block.reviewConfigs = data.delta.review_configs;
                            if (data.delta.timeout_seconds) {
                                block.timeoutSeconds = data.delta.timeout_seconds;
                                block.timeoutStartedAt = Date.now();
                            }
                        }
                        if (data.delta?.extras) {
                            block.extras = data.delta.extras;
                            const subtype = data.delta.extras.block_subtype;
                            if (subtype === 'user_stopped') {
                                block.type = 'terminal_user_stopped';
                            } else if (subtype === 'error') {
                                block.type = 'terminal_error';
                            }
                        }
                        if (data.delta?.decisions) {
                            block.decisions = data.delta.decisions;
                        }
                        // For file_processing status updates
                        if (data.delta?.status) {
                            block.processingStatus = data.delta.status;
                        }
                        if (data.delta?.message) {
                            block.processingMessage = data.delta.message;
                        }
                    }
                    break;
                }

                case 'content_block_stop': {
                    const block = inProgressBlocks.get(data.index);
                    if (block) {
                        // Try to parse partial JSON for tool_use
                        if (block.partialJson) {
                            try {
                                block.input = JSON.parse(block.partialJson);
                            } catch {
                                // Keep as string
                            }
                        }

                        if ((block.type === 'approval_result' || block.type === 'approval_timeout') && block.approval_key) {
                            const matchIdx = approvalBlockMap.get(block.approval_key);
                            if (matchIdx !== undefined && blocks[matchIdx]) {
                                blocks[matchIdx].isResolved = true;
                                if (block.decisions) {
                                    blocks[matchIdx].decisions = block.decisions;
                                    const editDecision = block.decisions.find(d => d.edited_action?.args?.answers);
                                    if (editDecision) {
                                        blocks[matchIdx].submittedAnswers = editDecision.edited_action.args.answers;
                                    }
                                }
                                inProgressBlocks.delete(data.index);
                                break;
                            }
                        }

                        if (block.type === 'tool_result' && block.tool_use_id) {
                            const toolUseBlockIndex = toolUseBlockMap.get(block.tool_use_id);
                            if (toolUseBlockIndex !== undefined && blocks[toolUseBlockIndex]) {
                                blocks[toolUseBlockIndex].toolResult = block.content;
                                blocks[toolUseBlockIndex].toolResultError = block.status === 'error' || block.status === 'cancelled';
                                if (block.artifact) {
                                    blocks[toolUseBlockIndex].artifact = block.artifact;
                                }
                                inProgressBlocks.delete(data.index);
                                break;
                            }
                        }

                        // Track tool_use blocks for later merging
                        if (block.type === 'tool_use' && block.id) {
                            toolUseBlockMap.set(block.id, blocks.length);
                        }

                        blocks.push(block);
                        if (block.type === 'approval_request' && block.approval_key) {
                            approvalBlockMap.set(block.approval_key, blocks.length - 1);
                        }
                        inProgressBlocks.delete(data.index);
                    }
                    break;
                }

                case 'group_start':
                    blocks.push({
                        type: 'group_start',
                        index: data.index,
                        parentToolUseId: data.parent_tool_use_id || null,
                    });
                    break;

                case 'group_end':
                    blocks.push({
                        type: 'group_end',
                        summary: data.summary || '',
                        index: data.index,
                        parentToolUseId: data.parent_tool_use_id || null,
                    });
                    break;

                case 'push_user_message':
                    blocks.push({ index: blocks.length, type: 'user', content: data.content });
                    break;

                case 'message_stop':
                    durationMs = data.duration_ms;
                    break;
            }
        } catch (e) {
            // Skip invalid JSON
        }
    }

    // Include all in-progress blocks for realtime display (sorted by index)
    const allBlocks = [...blocks];
    const streamingBlocks = Array.from(inProgressBlocks.values())
        .sort((a, b) => a.index - b.index)
        .map(block => ({ ...block, isStreaming: true }));

    // For streaming tool_result, try to merge into existing tool_use
    for (const streamBlock of streamingBlocks) {
        if (streamBlock.type === 'tool_result' && streamBlock.tool_use_id) {
            const toolUseBlockIndex = toolUseBlockMap.get(streamBlock.tool_use_id);
            if (toolUseBlockIndex !== undefined && allBlocks[toolUseBlockIndex]) {
                allBlocks[toolUseBlockIndex].toolResult = streamBlock.content;
                allBlocks[toolUseBlockIndex].toolResultStreaming = true;
                allBlocks[toolUseBlockIndex].toolResultError = streamBlock.status === 'error';
                if (streamBlock.artifact) {
                    allBlocks[toolUseBlockIndex].artifact = streamBlock.artifact;
                }
                continue;
            }
        }
        allBlocks.push(streamBlock);
    }

    allBlocks.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));

    const hasTerminalBlock = allBlocks.some(b => b.type === 'terminal_user_stopped' || b.type === 'terminal_error');
    if (hasTerminalBlock) {
        for (const block of allBlocks) {
            block.isStreaming = false;
        }
    }

    return { blocks: allBlocks, sessionId, durationMs };
}


// Format JSON for display
function formatJSON(obj) {
    if (typeof obj === 'string') {
        try {
            return JSON.stringify(JSON.parse(obj), null, 2);
        } catch {
            return obj;
        }
    }
    return JSON.stringify(obj, null, 2);
}

// Shimmer text component - shows a glowing animation from left to right when streaming
// Uses inline styles for proper shimmer effect with background-size for smooth animation
export function ShimmerText({ isStreaming, children, className = '' }) {
    return (
        <span className={`relative overflow-hidden inline-flex items-start text-left min-w-0 ${className}`}>
            {children}
            {isStreaming && (
                <span
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background: 'linear-gradient(90deg, transparent 0%, transparent 30%, rgba(255,255,255,0.8) 50%, transparent 70%, transparent 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s linear infinite',
                    }}
                />
            )}
        </span>
    );
}


const DOT_STYLES = {
    active: 'bg-amber-400',
    done: 'bg-green-500',
    stopped: 'bg-gray-400',
    error: 'bg-red-500',
};

function StatusDot({ status = 'done' }) {
    return (
        <span
            className={`inline-block rounded-full shrink-0 mt-[6.5px] ${DOT_STYLES[status] || DOT_STYLES.done}`}
            style={{ width: 7, height: 7, ...(status === 'active' ? { animation: 'pulseDot 1.2s ease-in-out infinite' } : {}) }}
        />
    );
}

// File Processing Block - Shows file extraction/processing status
// Styled similar to other blocks - just displays message from server
const FileProcessingBlock = memo(function FileProcessingBlock({ block }) {
    const isStreaming = block.isStreaming;
    const fileCount = block.files?.length || 0;
    const rawMessage = block.processingMessage || block.message || '';

    const message = rawMessage
        || (isStreaming ? `Đang xử lý ${fileCount || ''} tệp đính kèm...` : `Đã xử lý ${fileCount || ''} tệp đính kèm`);

    return (
        <div className="py-1.5">
            <div className="flex items-start gap-1.5 text-[13px] text-gray-500">
                <StatusDot status={isStreaming ? 'active' : 'done'} />
                <ShimmerText isStreaming={isStreaming}>
                    <span className="text-gray-500">{message}</span>
                </ShimmerText>
            </div>
        </div>
    );
});


// Thinking Block - Collapsible with markdown
// Auto-collapse when streaming completes, start collapsed if from history
// Memoized to prevent unnecessary re-renders
const ThinkingBlockFlat = memo(function ThinkingBlockFlat({ block, isFromHistory = false }) {
    const thinkingContent = block.thinking || '';
    const isStreaming = block.isStreaming;
    const contentRef = useRef(null);
    const shouldStickToBottomRef = useRef(true);
    const preset = useMarkdownPreset();
    const thinkingComponents = useMemo(() => buildThinkingMarkdownComponents(preset), [preset]);

    // Track previous streaming state to detect transition
    const prevStreamingRef = useRef(isStreaming);
    const hasCompletedRef = useRef(false);

    // Determine initial expanded state:
    // - From history: start collapsed
    // - Currently streaming: start expanded
    // - Completed (not streaming, not history): start collapsed
    const getInitialExpanded = () => {
        if (isFromHistory) return false;
        if (isStreaming) return true;
        return false; // Completed blocks start collapsed
    };

    const [isExpanded, setIsExpanded] = useState(getInitialExpanded);

    // Auto-collapse when streaming stops (transition from true to false/undefined)
    useEffect(() => {
        const wasStreaming = prevStreamingRef.current;
        const nowStreaming = isStreaming;

        // Detect streaming → done transition
        if (wasStreaming === true && !nowStreaming && !hasCompletedRef.current) {
            hasCompletedRef.current = true;
            // Small delay for visual feedback before collapse
            const timer = setTimeout(() => {
                setIsExpanded(false);
            }, 150);
            return () => clearTimeout(timer);
        }

        // If started streaming, expand it
        if (nowStreaming && !wasStreaming) {
            setIsExpanded(true);
            hasCompletedRef.current = false;
            shouldStickToBottomRef.current = true; // Reset stick-to-bottom when streaming starts
        }

        prevStreamingRef.current = nowStreaming;
    }, [isStreaming]);

    // Auto-scroll to bottom when content updates during streaming (rAF-coalesced)
    const scrollRafRef = useRef(null);
    useEffect(() => {
        if (isStreaming && isExpanded && contentRef.current && shouldStickToBottomRef.current) {
            if (scrollRafRef.current === null) {
                scrollRafRef.current = requestAnimationFrame(() => {
                    scrollRafRef.current = null;
                    if (contentRef.current) {
                        contentRef.current.scrollTop = contentRef.current.scrollHeight;
                    }
                });
            }
        }
        return () => {
            if (scrollRafRef.current !== null) {
                cancelAnimationFrame(scrollRafRef.current);
                scrollRafRef.current = null;
            }
        };
    }, [thinkingContent, isStreaming, isExpanded]);

    // Handle scroll - stop auto-scroll if user scrolls up
    const handleScroll = () => {
        if (!contentRef.current) return;
        const el = contentRef.current;
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8; // 8px threshold
        shouldStickToBottomRef.current = atBottom;
    };

    // Show even if empty when streaming
    if (!thinkingContent.trim() && !isStreaming) return null;

    return (
        <div className="py-1.5">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="group flex items-start gap-1.5 text-[13px] text-gray-500 hover:text-gray-700 transition-colors"
            >
                <StatusDot status={isStreaming ? 'active' : 'done'} />
                <span className="inline-flex items-center gap-1.5 flex-1 min-w-0">
                    <ShimmerText isStreaming={isStreaming}>
                        <span className="text-gray-500">{isStreaming ? 'Đang suy nghĩ' : 'Suy nghĩ'}</span>
                    </ShimmerText>
                    {isExpanded ? (
                        <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    ) : (
                        <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    )}
                </span>
            </button>

            {isExpanded && (
                <div
                    ref={contentRef}
                    onScroll={handleScroll}
                    className={preset.thinkingWrapper}
                >
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkBreaks, remarkMath]}
                        rehypePlugins={[rehypeRaw, rehypeKatex]}
                        components={thinkingComponents}
                    >
                        {thinkingContent}
                    </ReactMarkdown>
                </div>
            )}
        </div>
    );
});

// Group Block - Collapsible group of tool steps (thinking + tool_use + tool_result)
export const GroupBlock = memo(function GroupBlock({ summary, blocks, isDone = true, isFromHistory = false, sessionId = null, renderOptions = {} }) {
    const [isExpanded, setIsExpanded] = useState(!isDone);

    if (!blocks || blocks.length === 0) return null;

    return (
        <div className="py-1">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="group flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-gray-700 transition-colors w-full text-left"
            >
                <span className="text-gray-500">{summary || 'Processing...'}</span>
                <svg className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            </button>

            {isExpanded && (
                <div className="mt-1">
                    <div className="space-y-0">
                        {blocks.map((block, index) => renderBlock(block, index, {
                            isFromHistory,
                            sessionId,
                            ...renderOptions,
                        }))}
                        {isDone && (
                            <div className="flex items-center gap-1.5 text-[13px] text-gray-500 py-1.5">
                                <StatusDot status="done" />
                                <span>Hoàn thành</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});

// Live Group Block - Streaming group with summary header
// Expanded during streaming (max 3 visible), auto-collapses when group_end received
const MAX_VISIBLE_ITEMS = 3;

export const LiveGroupBlock = memo(function LiveGroupBlock({ blocks, isStreaming, summary: externalSummary, sessionId = null, renderOptions = {} }) {
    const [isExpanded, setIsExpanded] = useState(true);
    const [showAll, setShowAll] = useState(false);
    const prevStreamingRef = useRef(isStreaming);

    const isDone = !isStreaming;

    const liveSummary = useMemo(() => {
        if (externalSummary) return externalSummary;
        if (!blocks || blocks.length === 0) return 'Đang xử lý...';
        for (let i = blocks.length - 1; i >= 0; i--) {
            const b = blocks[i];
            if (b.type === 'thinking') continue;
            if (b.tool_content_message) return b.tool_content_message;
            if (b.name) return b.name;
        }
        return 'Đang xử lý...';
    }, [blocks, externalSummary]);

    useEffect(() => {
        if (prevStreamingRef.current && !isStreaming) {
            const timer = setTimeout(() => setIsExpanded(false), 300);
            return () => clearTimeout(timer);
        }
        prevStreamingRef.current = isStreaming;
    }, [isStreaming]);

    if (!blocks || blocks.length === 0) return null;

    const hasHidden = !isDone && !showAll && blocks.length > MAX_VISIBLE_ITEMS;
    const displayBlocks = (isDone || showAll)
        ? blocks
        : (blocks.length > MAX_VISIBLE_ITEMS ? blocks.slice(-MAX_VISIBLE_ITEMS) : blocks);

    return (
        <div className="py-1">
            <div className="group/header flex items-center gap-1.5 mb-1">
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="group flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-gray-700 transition-colors text-left"
                >
                    <ShimmerText isStreaming={!isDone}>
                        <span className="text-gray-500">{liveSummary}</span>
                    </ShimmerText>
                    <svg className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
                {!isDone && isExpanded && blocks.length > MAX_VISIBLE_ITEMS && (
                    <button
                        onClick={() => setShowAll(!showAll)}
                        className="flex-shrink-0 p-0.5 text-gray-400 hover:text-gray-600 transition-colors opacity-0 group-hover/header:opacity-100"
                        title={showAll ? 'Thu gọn' : 'Xem tất cả'}
                    >
                        {showAll ? (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5 5 5" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17l5-5 5 5" />
                            </svg>
                        )}
                    </button>
                )}
            </div>

            {isExpanded && (
                <div className="space-y-0">
                    {displayBlocks.map((block, index) => renderBlock(block, index, {
                        isFromHistory: isDone,
                        sessionId,
                        ...renderOptions,
                    }))}
                </div>
            )}

            {isDone && isExpanded && (
                <div className="flex items-center gap-1.5 text-[13px] text-gray-500 py-1.5">
                    <StatusDot status="done" />
                    <span>Hoàn thành</span>
                </div>
            )}
        </div>
    );
});

// Todo List Block - Special UI for write_todos/TodoWrite tool
// Collapsible, default expanded
function TodoListBlock({ todos, isStreaming = false, toolContentMessage = '' }) {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!todos || !Array.isArray(todos) || todos.length === 0) {
        return null;
    }

    // Status icons
    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return (
                    <span className="text-green-600 mr-2 leading-none">✓</span>
                );
            case 'in_progress':
                return (
                    <span className="w-2 h-2 mr-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                );
            case 'pending':
            default:
                return (
                    <span className="w-2 h-2 mr-2 border border-gray-400 rounded-full flex-shrink-0"></span>
                );
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'completed':
                return 'text-gray-400 line-through';
            case 'in_progress':
                return 'text-gray-800 font-medium';
            case 'pending':
            default:
                return 'text-gray-600';
        }
    };

    return (
        <div className="py-1.5">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="group flex items-start gap-1.5 text-[13px] text-gray-500 hover:text-gray-700 transition-colors"
            >
                <StatusDot status={isStreaming ? 'active' : 'done'} />
                <span className="inline-flex items-center gap-1.5 flex-1 min-w-0">
                    <ShimmerText isStreaming={isStreaming}>
                        <span className="text-gray-500">{toolContentMessage || 'Update Todos'}</span>
                    </ShimmerText>
                    {isExpanded ? (
                        <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    ) : (
                        <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    )}
                </span>
            </button>

            {isExpanded && (
                <div className="mt-1 pl-[14px]">
                    <ul className="space-y-1">
                        {todos.map((todo, index) => (
                            <li key={index} className="flex items-center text-sm">
                                {getStatusIcon(todo.status)}
                                <span className={getStatusStyle(todo.status)}>
                                    {todo.status === 'in_progress' && todo.activeForm ? todo.activeForm : todo.content}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

// Check if tool is a todo-related tool
function isTodoTool(toolName) {
    const name = (toolName || '').toLowerCase();
    // Match various patterns: TodoWrite, write_todos, todo_write, etc.
    return name.includes('todo') || name === 'write_todos';
}

function WebSearchBlock({ block, query, artifact, isActive, dotStatus, toolName }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const sources = artifact?.sources || [];
    const resultCount = sources.length;

    return (
        <div className="py-1.5">
            <div
                onClick={() => sources.length > 0 && setIsExpanded(!isExpanded)}
                className={`group flex items-start gap-1.5 text-[13px] text-gray-500 transition-colors min-w-0 ${sources.length > 0 ? 'cursor-pointer hover:text-gray-700' : ''}`}
            >
                <StatusDot status={dotStatus} />
                <span className="inline-flex items-center gap-1.5 flex-1 min-w-0">
                    <ShimmerText isStreaming={isActive}>
                        <span className="text-gray-500 truncate">{toolName}</span>
                    </ShimmerText>
                    {resultCount > 0 && (
                        <span className="text-gray-400 text-xs flex-shrink-0 whitespace-nowrap">{resultCount} kết quả</span>
                    )}
                    {sources.length > 0 && (
                        isExpanded ? (
                            <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        ) : (
                            <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        )
                    )}
                </span>
            </div>

            {isExpanded && sources.length > 0 && (
                <div className="group-expanded-content">
                <div className="mt-2 border border-gray-200 rounded-xl overflow-hidden">
                    <div className="max-h-48 overflow-y-auto">
                        {sources.map((source, idx) => (
                            <a
                                key={idx}
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-xs text-gray-600 hover:text-blue-600 hover:bg-gray-50 py-2 px-3 transition-colors"
                            >
                                <img
                                    src={source.favicon || `https://www.google.com/s2/favicons?domain=${source.domain}&sz=16`}
                                    alt=""
                                    className="w-4 h-4 rounded-sm flex-shrink-0"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                                <span className="truncate flex-1">{source.title || source.url}</span>
                                <span className="text-gray-400 flex-shrink-0">{source.domain}</span>
                            </a>
                        ))}
                    </div>
                </div>
                </div>
            )}
        </div>
    );
}

// Tool Call Block - Shows both Request and Response in one unified block
// Auto-collapse when streaming completes
function ToolCallBlock({ block, isFromHistory = false }) {
    const toolName = block.tool_content_message || formatToolName(block.name);
    const isStreaming = block.isStreaming;
    const hasToolResult = block.toolResult !== null && block.toolResult !== undefined;
    const isError = block.toolResultError;
    const canExpand = DEBUG_ENABLED || EXPANDABLE_TOOLS.has(block.name);

    const prevStreamingRef = useRef(isStreaming);
    const hasCompletedRef = useRef(false);

    const [isExpanded, setIsExpanded] = useState(() => {
        if (!canExpand || isFromHistory) return false;
        return isStreaming;
    });

    useEffect(() => {
        if (!canExpand) return;
        const wasStreaming = prevStreamingRef.current;
        const nowStreaming = isStreaming;

        if (wasStreaming === true && !nowStreaming && !hasCompletedRef.current) {
            hasCompletedRef.current = true;
            const timer = setTimeout(() => {
                setIsExpanded(false);
            }, 150);
            return () => clearTimeout(timer);
        }

        if (nowStreaming && !wasStreaming) {
            setIsExpanded(true);
            hasCompletedRef.current = false;
        }

        prevStreamingRef.current = nowStreaming;
    }, [isStreaming, canExpand]);

    // Show partial JSON while streaming, or parsed input when complete
    const displayInput = isStreaming && block.partialJson
        ? block.partialJson
        : block.input || {};

    const isActive = !hasToolResult && !isFromHistory;

    const dotStatus = isActive ? 'active'
        : isError ? 'error'
        : 'done';

    if (isTodoTool(block.name)) {
        const todos = displayInput?.todos || [];
        return <TodoListBlock todos={todos} isStreaming={isActive} toolContentMessage={block.tool_content_message} />;
    }

    if (block.name === 'web_search' || block.artifact?.sources?.length > 0) {
        const query = displayInput?.query || displayInput?.tickers?.join(', ') || toolName;
        return <WebSearchBlock block={block} query={query} artifact={block.artifact} isActive={isActive} dotStatus={dotStatus} toolName={toolName} />;
    }

    const inlineCfg = INLINE_TOOL_DISPLAY[block.name];
    const inlineValue = inlineCfg ? (displayInput?.[inlineCfg.param] || '') : '';

    return (
        <div className="py-1.5">
            <div
                onClick={canExpand ? () => setIsExpanded(!isExpanded) : undefined}
                className={`group flex items-start gap-1.5 text-[13px] text-gray-500 transition-colors min-w-0 ${canExpand ? 'cursor-pointer hover:text-gray-700' : ''}`}
            >
                <StatusDot status={dotStatus} />
                <ShimmerText isStreaming={isActive}>
                    <span className="text-gray-500">{toolName}</span>
                </ShimmerText>
                {inlineValue && (
                    <span className="text-gray-500 font-normal truncate min-w-0 flex-1">{inlineValue}</span>
                )}
                {canExpand && (
                    isExpanded ? (
                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    ) : (
                        <svg className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    )
                )}
            </div>

            {canExpand && isExpanded && (
                <div className="group-expanded-content">
                <div className="mt-2 space-y-3">
                    <div>
                        <div className="text-xs font-semibold text-gray-500 mb-1">Request</div>
                        <pre className="text-xs font-mono bg-gray-50 p-2 rounded text-gray-700 whitespace-pre-wrap break-all">
                            {formatJSON(displayInput)}
                        </pre>
                    </div>

                    {hasToolResult && (
                        <div>
                            <div className="text-xs font-semibold text-gray-500 mb-1">
                                Response
                            </div>
                            <pre className={`text-xs font-mono p-2 rounded whitespace-pre-wrap break-all ${isError ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-700'}`}>
                                {block.toolResult}
                            </pre>
                        </div>
                    )}
                </div>
                </div>
            )}
        </div>
    );
}

// Task Block - Shows Task tool with nested child blocks (sub-agent output)
// Expandable to show child blocks (thinking, tool calls, text from sub-agent)
function TaskBlock({ block, isFromHistory = false, renderOptions = {} }) {
    const childBlocks = block.childBlocks || [];
    const hasChildBlocks = childBlocks.length > 0;
    const hasToolResult = block.toolResult !== null && block.toolResult !== undefined;
    const isStreaming = !hasToolResult && !isFromHistory && block.isStreaming !== false;

    // Track previous streaming state to detect transition
    const prevStreamingRef = useRef(isStreaming);
    const hasCompletedRef = useRef(false);

    // Determine initial expanded state
    const getInitialExpanded = () => {
        if (isFromHistory) return false;
        if (isStreaming) return true;
        return false; // Completed blocks start collapsed
    };

    const [isExpanded, setIsExpanded] = useState(getInitialExpanded);

    // Auto-collapse when streaming stops
    useEffect(() => {
        const wasStreaming = prevStreamingRef.current;
        const nowStreaming = isStreaming;

        if (wasStreaming === true && !nowStreaming && !hasCompletedRef.current) {
            hasCompletedRef.current = true;
            const timer = setTimeout(() => {
                setIsExpanded(false);
            }, 150);
            return () => clearTimeout(timer);
        }

        if (nowStreaming && !wasStreaming) {
            setIsExpanded(true);
            hasCompletedRef.current = false;
        }

        prevStreamingRef.current = nowStreaming;
    }, [isStreaming]);

    const isActive = !hasToolResult && !isFromHistory;
    const isError = block.toolResultError;

    const dotStatus = isActive ? 'active'
        : isError ? 'error'
        : 'done';

    if (block.name === 'task' || block.name === 'Task') {
        console.log('[TaskBlock]', block.id, 'hasToolResult=', hasToolResult, 'toolResult=', block.toolResult?.substring?.(0, 50) || block.toolResult, 'isStreaming=', isStreaming, 'dotStatus=', dotStatus);
    }

    return (
        <div className="py-1.5">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="group flex items-start gap-1.5 text-[13px] text-gray-500 hover:text-gray-700 transition-colors"
            >
                <StatusDot status={dotStatus} />
                <ShimmerText isStreaming={isActive}>
                    <span className="text-gray-500">{block.tool_content_message || formatToolName(block.name)}</span>
                </ShimmerText>
                {hasChildBlocks && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">
                        {childBlocks.filter(b => b.type === 'tool_use').length}
                    </span>
                )}
                {isExpanded ? (
                    <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                ) : (
                    <svg className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                )}
            </button>

            {isExpanded && (
                <div className="mt-2 ml-3 pl-3 border-l-2 border-gray-200 space-y-1">
                    {/* Child blocks from sub-agent - render directly without "Task" title */}
                    {hasChildBlocks && childBlocks.map((childBlock, idx) => (
                        <div key={childBlock.id || childBlock.index || `child-${idx}`}>
                            {renderBlock(childBlock, idx, {
                                ...renderOptions,
                                isFromHistory,
                                isChildOfTask: true
                            })}
                        </div>
                    ))}

                    {/* Tool result (final output from sub-agent) — render as markdown */}
                    {hasToolResult && (
                        <div className="mt-2">
                            <div className="text-xs font-semibold text-gray-500 mb-1">Result</div>
                            <div className="bg-gray-50 p-3 rounded max-h-[400px] overflow-y-auto">
                                <TextBlockFlat block={{ content: String(block.toolResult || ''), isStreaming: false }} />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


// Tool Result Block - Start collapsed (result of completed tool)
// This is a fallback for unmerged tool_result blocks
function ToolResultBlockFlat({ block, isFromHistory = false }) {
    // Tool results are always completed, start collapsed
    const [isExpanded, setIsExpanded] = useState(false);

    // Display raw content without parsing
    const displayContent = block.content || '';

    // Simple: use name if valid, otherwise tool_use_id, otherwise empty
    const toolName = (block.name && block.name !== 'unknown')
        ? formatToolName(block.name)
        : (block.tool_use_id ? block.tool_use_id.slice(0, 8) : '');

    return (
        <div className="py-1.5">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="group flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
                <span className="text-gray-500">{toolName || 'Result'}</span>
                {/* Arrow: hidden when collapsed, show on hover; always show when expanded */}
                {isExpanded ? (
                    <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                ) : (
                    <svg className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                )}
            </button>

            {isExpanded && (
                <div className="mt-2">
                    <div className="text-xs font-semibold text-gray-500 mb-1">Response</div>
                    <pre className="text-xs font-mono bg-gray-50 p-2 rounded text-gray-700 max-h-[300px] overflow-y-auto whitespace-pre-wrap break-all">
                        {displayContent}
                    </pre>
                </div>
            )}
        </div>
    );
}

// Note: parseArtifacts has been replaced by parseContentWithWidgets from widgetParser
// which handles both legacy [artifact:type:id] and new <widget>json</widget> patterns

// Text Block - Markdown rendered with syntax highlighting and math support
// Supports both legacy [artifact:type:id] and new <widget>json</widget> patterns
// Memoized to prevent unnecessary re-renders when parent re-renders
const TextBlockFlat = memo(function TextBlockFlat({ block }) {
    const textContent = block.content || block.text || '';
    const isStreaming = block.isStreaming;
    const preset = useMarkdownPreset();
    const textComponents = useMemo(() => buildTextMarkdownComponents(preset), [preset]);
    const parts = useMemo(() => parseContentWithWidgets(textContent, isStreaming), [textContent, isStreaming]);

    if (!textContent.trim() && !isStreaming) return null;

    return (
        <div className="py-1">
            {parts.map((part, index) => {
                if (part.type === 'text') {
                    if (!part.content.trim()) return null;

                    return (
                        <div key={index} className={preset.wrapper}>
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm, remarkBreaks, remarkMath]}
                                rehypePlugins={[rehypeRaw, rehypeKatex]}
                                components={textComponents}
                            >
                                {part.content}
                            </ReactMarkdown>
                        </div>
                    );
                } else if (part.type === 'widget') {
                    // Render widget using unified WidgetRenderer
                    // Handles both realtime (api + params) and static (artifact_id) modes
                    return <WidgetRenderer key={index} config={part.config} />;
                } else if (part.type === 'widget_loading') {
                    // Render skeleton for incomplete widget during streaming
                    return (
                        <div key={index} className="my-3">
                            <WidgetSkeleton type={part.widgetType} />
                        </div>
                    );
                } else if (part.type === 'visualization') {
                    return (
                        <div key={index} className="my-3">
                            <VisualizeWidget artifactId={part.artifactId} />
                        </div>
                    );
                } else if (part.type === 'artifact') {
                    if (part.artifactType === 'chart') {
                        return <ChartBlock key={index} block={{ artifact_id: part.artifactId }} />;
                    }
                    if (part.artifactType === 'widget') {
                        return <WidgetRenderer key={index} config={{ artifact_id: part.artifactId }} />;
                    }
                    return null;
                }
                return null;
            })}
        </div>
    );
});

// Status Block - Show status messages
function StatusBlockFlat({ block }) {
    const statusContent = block.content || block.status || '';

    if (!statusContent.trim()) return null;

    return (
        <div className="py-1.5">
            <div className="text-sm text-blue-600 italic px-3 py-1.5 bg-blue-50 rounded-lg inline-block">
                {statusContent}
            </div>
        </div>
    );
}

function TerminalUserStoppedBlock({ block }) {
    const message = block.content || block.text || 'Người dùng đã dừng cuộc trò chuyện. Gửi tin nhắn mới để tiếp tục';

    return (
        <div className="py-[5px]">
            <div className="inline-flex w-max max-w-full items-center px-3 py-[5px] rounded-full" style={{ backgroundColor: 'rgba(239, 162, 1, 0.1)' }}>
                <span className="text-[13px] leading-[18px]" style={{ color: '#efa201' }}>{message}</span>
            </div>
        </div>
    );
}

function TerminalErrorBlock({ block, onRetry }) {
    const message = block.content || block.text || 'Có lỗi xảy ra, vui lòng thử lại';
    const extras = block.extras || {};
    const canRetry = extras.can_retry !== false;

    return (
        <div className="py-2">
            <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 rounded-lg inline-flex text-sm text-red-600">
                <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
                <span>{message}</span>
                {canRetry && onRetry && (
                    <button
                        onClick={onRetry}
                        className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 bg-white hover:bg-red-50 border border-red-200 rounded-md px-2 py-1 transition-colors ml-1"
                    >
                        <RefreshCw size={12} />
                        <span>Tiếp tục</span>
                    </button>
                )}
            </div>
        </div>
    );
}

// Unknown/Generic Block - Collapsible for any unknown type
function UnknownBlockFlat({ block }) {
    const [isExpanded, setIsExpanded] = useState(true);
    const content = block.content || block.thinking || block.text || '';
    const blockType = block.type || 'unknown';

    if (!content.trim()) return null;

    return (
        <div className="py-1.5">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="group flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
                <span className="text-gray-500">{blockType}</span>
                {/* Arrow: hidden when collapsed, show on hover; always show when expanded */}
                {isExpanded ? (
                    <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                ) : (
                    <svg className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                )}
            </button>

            {isExpanded && (
                <div className="mt-2">
                    <pre className="text-xs font-mono bg-gray-50 p-2 rounded text-gray-700 max-h-[300px] overflow-y-auto whitespace-pre-wrap break-all">
                        {content}
                    </pre>
                </div>
            )}
        </div>
    );
}


// Parameter Display - shows formatted value based on type
function ItemDisplay({ value }) {
    if (value === null || value === undefined) return <span className="text-gray-400">-</span>;

    if (Array.isArray(value)) {
        if (value.every(v => typeof v === 'string')) {
            return (
                <ul className="list-none space-y-1">
                    {value.map((v, idx) => (
                        <li key={idx} className="text-sm text-gray-900">{v}</li>
                    ))}
                </ul>
            );
        }
        return <span className="text-gray-900">{value.join(', ')}</span>;
    }

    if (typeof value === 'object') {
        return (
            <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto text-gray-900">
                {JSON.stringify(value, null, 2)}
            </pre>
        );
    }

    if (typeof value === 'boolean') {
        return <span className="text-gray-900">{value ? 'Có' : 'Không'}</span>;
    }

    if (typeof value === 'number') {
        return <span className="text-gray-900">{value.toLocaleString('vi-VN')}</span>;
    }

    return <span className="text-gray-900">{String(value)}</span>;
}

function ItemField({ item }) {
    return (
        <div className="flex items-start text-sm gap-3">
            <div className="text-xs text-gray-500 pt-0.5 w-[100px] shrink-0">
                {item.name}
            </div>
            <div className="flex-1 text-gray-800">
                <ItemDisplay value={item.value} />
            </div>
        </div>
    );
}


export function extractQuestions(questionAction) {
    let questions = questionAction?.args?.questions;
    if (!questions && questionAction?.args) {
        const args = questionAction.args;
        if (args.question && args.options) {
            questions = [{
                question: args.question,
                header: args.header || '',
                options: args.options || [],
                multi_select: args.multi_select || false
            }];
        }
    }
    return questions || [];
}

function ResolvedQuestionBlock({ block }) {
    const questionAction = block.actionRequests?.find(a => a.name === 'ask_user_question');
    const questions = extractQuestions(questionAction);
    const submittedAnswers = block.submittedAnswers || {};

    if (questions.length === 0) return null;

    return (
        <div className="py-2">
            <div className="border border-gray-200 rounded-xl px-4 py-3 space-y-2">
                {questions.map((q, idx) => (
                    <div key={idx}>
                        <div className="text-sm font-semibold text-gray-700 leading-relaxed">
                            {q.question}
                        </div>
                        <div className="text-sm text-gray-500 mt-0.5">
                            {submittedAnswers[q.question] || '[No preference]'}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function useCountdown(timeoutSeconds, startedAt) {
    const [remaining, setRemaining] = useState(() => {
        if (!timeoutSeconds || !startedAt) return null;
        const elapsed = Math.floor((Date.now() - startedAt) / 1000);
        return Math.max(timeoutSeconds - elapsed, 0);
    });

    useEffect(() => {
        if (!timeoutSeconds || !startedAt) return;
        const tick = () => {
            const elapsed = Math.floor((Date.now() - startedAt) / 1000);
            setRemaining(Math.max(timeoutSeconds - elapsed, 0));
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [timeoutSeconds, startedAt]);

    return remaining;
}

// Approval Request Block - Shared component for approval UI (Merged single card)
export function ApprovalRequestBlock({ block, onApprove, onReject, isSubmitting }) {
    const countdown = useCountdown(block.timeoutSeconds, block.timeoutStartedAt);

    const isQuestionTool = block.actionRequests?.some(
        action => action.name === 'ask_user_question'
    );

    if (isQuestionTool) {
        const questionAction = block.actionRequests.find(a => a.name === 'ask_user_question');
        let questions = extractQuestions(questionAction);

        return (
            <div className="w-full">
                <QuestionUI
                    questions={questions}
                    onSubmit={(data) => {
                        block.submittedAnswers = data.answers;
                        onApprove(data);
                    }}
                    isSubmitting={isSubmitting}
                />
            </div>
        );
    }

    const [isEditing, setIsEditing] = useState(false);
    const [editFeedback, setEditFeedback] = useState('');

    if (block.actionRequests && block.actionRequests.length > 0) {
        const reviewConfigs = block.reviewConfigs || [];
        const allAllowed = new Set();
        if (reviewConfigs.length > 0) {
            for (const rc of reviewConfigs) {
                for (const d of (rc.allowed_decisions || [])) {
                    allAllowed.add(d);
                }
            }
        }
        const canApprove = allAllowed.size === 0 || allAllowed.has('approve');
        const canEdit = allAllowed.size === 0 || allAllowed.has('edit');
        const canReject = allAllowed.size === 0 || allAllowed.has('reject');

        const handleApprove = () => {
            if (isEditing && editFeedback.trim()) {
                if (onApprove) onApprove({ userEditContent: editFeedback.trim() });
            } else {
                if (onApprove) onApprove({});
            }
        };

        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                handleApprove();
            }
        };

        return (
            <>
            <style>{`
                @keyframes approvalBeamRotate {
                    0% { transform: rotate(0deg); opacity: 1; }
                    40% { transform: rotate(360deg); opacity: 1; }
                    45% { transform: rotate(360deg); opacity: 0; }
                    95% { transform: rotate(360deg); opacity: 0; }
                    100% { transform: rotate(360deg); opacity: 1; }
                }
            `}</style>
            <div className="w-full" style={{ position: 'relative', padding: '1.5px', borderRadius: '1rem', overflow: 'hidden' }}>
                {/* LED beam border - centering wrapper keeps gradient square */}
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <div style={{
                        width: '150%',
                        aspectRatio: '1',
                        flexShrink: 0,
                        background: 'conic-gradient(from 0deg, transparent 0%, rgba(59,130,246,0.8) 8%, transparent 16%, transparent 50%, rgba(249,115,22,0.7) 58%, transparent 66%)',
                        animation: 'approvalBeamRotate 10s linear infinite',
                    }} />
                </div>
                <div className="relative rounded-2xl shadow-lg shadow-blue-100/30 p-5 sm:p-6" style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #fff7ed 100%)' }}>
                {/* Dialog Title */}
                <div className="flex items-center justify-between -mt-1 mb-4">
                    <div className="text-xs font-medium text-gray-400">Cho phép Simplize thực hiện hành động</div>
                    {countdown !== null && (
                        <div className={`text-xs font-mono tabular-nums ${countdown <= 10 ? 'text-red-500' : 'text-gray-400'}`}>
                            {countdown}s
                        </div>
                    )}
                </div>

                {/* Tools List - Timeline style */}
                <div className="relative mb-5">
                    {/* Connecting line */}
                    {block.actionRequests.length > 1 && (
                        <div className="absolute left-[3.5px] top-[12px] bottom-[12px] w-px bg-gray-200" />
                    )}
                    <div className="space-y-4">
                    {block.actionRequests.map((action, idx) => {
                        const display = action.display;

                        // Fallback if no display metadata
                        if (!display) {
                            return (
                                <div key={action.id || idx} className="relative flex gap-3">
                                    <div className="relative z-10 flex-shrink-0 mt-[7px] w-2 h-2 rounded-full border-[1.5px] border-blue-400 bg-white" />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-xs font-medium text-gray-500">Tool:</span>
                                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-mono">
                                                {formatToolName(action.name)}
                                            </span>
                                        </div>
                                        <pre className="text-xs font-mono bg-gray-50 p-2 rounded overflow-x-auto text-gray-700 max-h-[100px] overflow-y-auto">
                                            {JSON.stringify(action.args, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div key={action.id || idx} className="relative flex gap-3">
                                <div className="relative z-10 flex-shrink-0 mt-[7px] w-2 h-2 rounded-full border-[1.5px] border-blue-400 bg-white" />
                                <div className="flex-1 min-w-0">
                                    {/* Tool Header */}
                                    <div className="mb-2">
                                        <h3 className="font-medium text-gray-700 text-sm">
                                            {display.title}
                                        </h3>
                                    </div>

                                    {/* Tool Items */}
                                    {display.items && display.items.length > 0 && (
                                        <div className="space-y-1.5">
                                            {display.items.map((item, idx) => (
                                                <ItemField key={idx} item={item} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    </div>
                </div>

                {/* Edit feedback textarea */}
                {isEditing && (
                    <div className="mb-4">
                        <div className="relative">
                            <textarea
                                value={editFeedback}
                                onChange={(e) => setEditFeedback(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="w-full text-sm bg-white p-3 pr-10 rounded-lg border border-gray-300 text-gray-700 min-h-[80px] max-h-[160px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Thêm hướng dẫn hoặc phản hồi... (Ctrl+Enter để xác nhận)"
                                autoFocus
                            />
                            <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                                ⌘↵
                            </div>
                        </div>
                    </div>
                )}

                {/* Actions - Only show allowed decisions */}
                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    {canApprove && (
                        <button
                            onClick={handleApprove}
                            disabled={isSubmitting}
                            className="w-full sm:w-auto px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 shadow-sm shadow-blue-200/50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all"
                        >
                            {isSubmitting ? '...' : (editFeedback.trim() ? 'Xác nhận & Gửi' : 'Xác nhận')}
                        </button>
                    )}
                    {canEdit && (
                        !isEditing ? (
                            <button
                                onClick={() => setIsEditing(true)}
                                disabled={isSubmitting}
                                className="w-full sm:w-auto px-4 py-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                            >
                                Chỉnh sửa
                            </button>
                        ) : (
                            <button
                                onClick={() => { setIsEditing(false); setEditFeedback(''); }}
                                disabled={isSubmitting}
                                className="w-full sm:w-auto px-4 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                            >
                                Hủy
                            </button>
                        )
                    )}
                    {canReject && (
                        <button
                            onClick={() => onReject && onReject()}
                            disabled={isSubmitting}
                            className="w-full sm:w-auto px-4 py-2 bg-orange-50 text-orange-600 rounded-xl hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                        >
                            {isSubmitting ? '...' : 'Từ chối'}
                        </button>
                    )}
                </div>
                </div>
            </div>
            </>
        );
    }

    // Fallback for legacy approval requests without action_requests
    return null;
}

// Render a single block based on type
export function renderBlock(block, index, { onApprove, onReject, onEdit, onRetry, isSubmitting, showApprovalUI, isFromHistory = false, isChildOfTask = false, sessionId = null } = {}) {
    const renderOptions = { onApprove, onReject, onEdit, onRetry, isSubmitting, showApprovalUI, sessionId };

    switch (block.type) {
        case 'thinking':
            return <ThinkingBlockFlat key={`block-${index}`} block={block} isFromHistory={isFromHistory} />;

        case 'attachments':
            return <FilesBlock key={`block-${index}`} files={block.files} sessionId={sessionId} />;

        case 'tool_use': {
            if (block.name === 'ask_user_question') {
                if (isFromHistory) {
                    const answers = block.input?.answers;
                    if (answers && Object.keys(answers).length > 0) {
                        const resolvedBlock = {
                            actionRequests: [{ name: 'ask_user_question', args: block.input }],
                            submittedAnswers: answers,
                            isResolved: true,
                        };
                        return <ResolvedQuestionBlock key={`block-${index}`} block={resolvedBlock} />;
                    }
                }
                return null;
            }
            // Use TaskBlock for task tools (have nested child blocks)
            // But skip if this is already a child of a Task (to avoid nested "Task" labels)
            const isTask = block.name === 'task' || block.name === 'Task';
            if (isTask && block.childBlocks && !isChildOfTask) {
                return <TaskBlock key={`block-${index}`} block={block} isFromHistory={isFromHistory} renderOptions={renderOptions} />;
            }
            // For child Task blocks or non-Task tool_use, use regular ToolCallBlock
            return <ToolCallBlock key={`block-${index}`} block={block} isFromHistory={isFromHistory} />;
        }

        case 'tool_result':
            if (block._merged) return null;
            return <ToolResultBlockFlat key={`block-${index}`} block={block} isFromHistory={isFromHistory} />;

        case 'text':
            return <TextBlockFlat key={`block-${index}`} block={block} />;

        case 'status':
            return <StatusBlockFlat key={`block-${index}`} block={block} />;

        case 'terminal_user_stopped':
            return <TerminalUserStoppedBlock key={`block-${index}`} block={block} />;

        case 'terminal_error':
            return <TerminalErrorBlock key={`block-${index}`} block={block} onRetry={onRetry} />;

        case 'approval_request': {
            const isQuestion = block.actionRequests?.some(a => a.name === 'ask_user_question');
            if (block.isResolved && isQuestion) {
                return <ResolvedQuestionBlock key={`block-${index}`} block={block} />;
            }
            if (!showApprovalUI || block.isResolved) return null;
            if (isQuestion) return null;
            return (
                <div key={`block-${index}`} className="pt-4">
                    <ApprovalRequestBlock
                        block={block}
                        onApprove={onApprove}
                        onReject={onReject}
                        onEdit={onEdit}
                        isSubmitting={isSubmitting}
                    />
                </div>
            );
        }

        case 'approval_result':
        case 'approval_timeout':
            return null;

        case 'user':
            return <UserMessageBlock key={`block-${index}`} content={block.content} />;

        case 'chart':
            // Chart visualization block
            return <ChartBlock key={`block-${index}`} block={block} />;

        case 'file_processing':
            // File processing status block
            return <FileProcessingBlock key={`block-${index}`} block={block} />;

        default:
            // Unknown type - use generic collapsible block
            return <UnknownBlockFlat key={`block-${index}`} block={block} />;
    }
}

const ATTACHMENT_ICON_COLORS = {
    pdf: 'text-red-500', image: 'text-blue-500', docx: 'text-blue-600', doc: 'text-blue-600',
    xlsx: 'text-green-600', pptx: 'text-orange-500', txt: 'text-gray-500', csv: 'text-green-500',
    md: 'text-blue-400', json: 'text-yellow-600', code: 'text-purple-500', file: 'text-gray-500',
};

const ATTACHMENT_TYPE_LABELS = {
    pdf: 'PDF', image: 'Image', docx: 'DOCX', doc: 'DOC',
    xlsx: 'XLSX', pptx: 'PPTX', txt: 'TXT', csv: 'CSV',
    md: 'Markdown', json: 'JSON', code: 'Code', file: 'File',
};

function formatFileSize(bytes) {
    if (!bytes || bytes <= 0) return null;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function AttachmentChip({ attachment, onClick }) {
    const typeLabel = ATTACHMENT_TYPE_LABELS[attachment.icon_type] || 'File';
    const sizeLabel = formatFileSize(attachment.file_size);
    const subtitle = [typeLabel, sizeLabel].filter(Boolean).join(' · ');

    return (
        <button onClick={onClick}
            className="flex items-center gap-2.5 pl-3 pr-4 py-2 bg-gray-100 rounded-xl text-left hover:bg-gray-200 transition-colors min-w-0">
            <FileText className={`w-5 h-5 flex-shrink-0 ${ATTACHMENT_ICON_COLORS[attachment.icon_type] || 'text-gray-500'}`} />
            <div className="flex flex-col min-w-0">
                <span className="text-[13px] text-gray-800 font-medium truncate max-w-[180px]">
                    {attachment.filename || 'File'}
                </span>
                <span className="text-[11px] text-gray-400">{subtitle}</span>
            </div>
        </button>
    );
}

export const UserMessageBlock = memo(function UserMessageBlock({ content, attachments, onAttachmentClick }) {
    const preset = useMarkdownPreset();
    const hasAttachments = attachments && attachments.length > 0;
    if (!content?.trim() && !hasAttachments) return null;

    return (
        <div className="flex flex-col items-end gap-2 py-2">
            {hasAttachments && (
                <div className="flex flex-wrap justify-end gap-2 max-w-[85%]">
                    {attachments.map((att, idx) => (
                        <AttachmentChip key={att.id || idx} attachment={att}
                            onClick={() => onAttachmentClick?.(att)} />
                    ))}
                </div>
            )}
            {content?.trim() && (
                <div className={preset.userBubble}>
                    <div className={preset.userBubbleText} style={{ overflowWrap: 'anywhere', color: '#34322d' }}>
                        {content}
                    </div>
                </div>
            )}
        </div>
    );
});

// AI Message Block - renders content blocks
// Memoized to prevent unnecessary re-renders when parent re-renders
export const AIMessageBlock = memo(function AIMessageBlock({
    blocks,
    onApprove,
    onReject,
    onEdit,
    onRetry,
    isSubmitting,
    showApprovalUI,
    isFromHistory = false,
    sessionId = null,
}) {
    if (!blocks || blocks.length === 0) return null;

    return (
        <div className="space-y-0">
            {blocks.map((block, index) => renderBlock(block, index, {
                onApprove,
                onReject,
                onEdit,
                onRetry,
                isSubmitting,
                showApprovalUI,
                isFromHistory,
                sessionId,
            }))}
        </div>
    );
});

// Streaming Loading Indicator - bouncing dots shown at bottom while streaming
export function StreamingLoadingIndicator() {
    return (
        <div className="flex gap-3 items-start py-2">
            <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '600ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms', animationDuration: '600ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms', animationDuration: '600ms' }} />
            </span>
        </div>
    );
}

// Main Component - Flat Timeline View (for SSE streaming)
// isFromHistory = false for live streaming (active blocks expand/auto-collapse)
// isStreaming = true shows loading indicator at bottom until message_stop
// Memoized to prevent unnecessary re-renders
const MessageBlockRenderer = memo(function MessageBlockRenderer({
    rawEvents,
    onApprove,
    onReject,
    onEdit,
    onRetry,
    isSubmitting,
    showApprovalUI,
    isFromHistory = false,
    isStreaming = false,
    sessionId: sessionIdProp = null,
}) {
    const { blocks: rawBlocks, sessionId: parsedSessionId, durationMs } = useMemo(() => parseSSEEvents(rawEvents), [rawEvents]);
    const sessionId = parsedSessionId || sessionIdProp;

    // Merge tool blocks and group child blocks by parentToolUseId
    const blocks = useMemo(() => mergeToolBlocks(rawBlocks), [rawBlocks]);

    // Show loading if: streaming active AND message_stop not received yet (no durationMs)
    const hasPendingApproval = blocks.some(b => b.type === 'approval_request' && !b.isResolved);
    const showStreamingLoading = isStreaming && !durationMs && !hasPendingApproval;

    const groupedItems = useMemo(() => {
        const items = [];
        let currentGroup = null;

        for (const block of blocks) {
            if (block.type === 'group_start') {
                if (currentGroup) items.push(currentGroup);
                currentGroup = { type: '_group', blocks: [], summary: '', isOpen: true };
                continue;
            }
            if (block.type === 'group_end') {
                if (currentGroup) {
                    currentGroup.summary = block.summary || currentGroup.summary;
                    currentGroup.isOpen = false;
                    items.push(currentGroup);
                    currentGroup = null;
                }
                continue;
            }
            if (block.type === 'approval_request') {
                items.push(block);
            } else if (currentGroup) {
                currentGroup.blocks.push(block);
            } else {
                items.push(block);
            }
        }
        if (currentGroup) {
            if (!isStreaming) currentGroup.isOpen = false;
            items.push(currentGroup);
        }
        return items;
    }, [blocks, isStreaming]);

    const renderOptions = { onApprove, onReject, onEdit, onRetry, isSubmitting, showApprovalUI, isFromHistory, sessionId };

    return (
        <>
            <div className="space-y-0">
                {groupedItems.map((item, index) => {
                    if (item.type === '_group') {
                        return (
                            <LiveGroupBlock
                                key={`group-${index}`}
                                blocks={item.blocks}
                                isStreaming={item.isOpen}
                                sessionId={sessionId}
                                renderOptions={renderOptions}
                            />
                        );
                    }
                    return renderBlock(item, index, renderOptions);
                })}
            </div>

            {showStreamingLoading && <StreamingLoadingIndicator />}

            {durationMs && (
                <div className="text-xs text-gray-400 mt-2">
                    Completed in {(durationMs / 1000).toFixed(2)}s
                </div>
            )}
        </>
    );
});

export default MessageBlockRenderer;
